import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { fetchJsonFromUrl } from "../../../utils/commonComponent/handlejsonData";
import DownloadPdfLoaderModal from "../../../utils/commonComponent/downloadPdfLoader";
import {
  fetchEmployeeDetails,
  fetchEmployeeOtherData,
  fetchEmpTrainingFeedback,
  fetchAssetRecords,
  fetchSurveyorMonitoringReport,
  fetchSurveyorItems,
  fetchEmployeeNdtMethods,
  fetchEmployeeQualification,
  fetchEmployeeForms,
  fetchEmployeeJobDescription,
} from "./fetchHandler";
import { generateTrainingFeedbackPDF } from "./TrainingFeedback/EditTrainingFeedbackPrint";
import { generateAssetIssueRecordPDF } from "./AssetIssueRecord/editAssetissuerecordprint";
import { generateSurveyorMonitoringReportPDF } from "./SurveyorMonitoringReport/EditSurveyorMonitoringReportPrint";
import { generateNdtQualificationPDF } from "./NdtQualification/EditNdtQualificationPrint";
import {
  REFERENCE_DOC_DEFS,
  flattenQuestionKeys,
  mergeWithDefs,
} from "./const/surveyorReportDefs";
import { NDT_METHOD_TYPES, mergeNdtMethodRows, emptyNdtMethodRow } from "./const/ndtMethodDefs";
import { toast } from "react-toastify";
import { generateEmpTrainingPDF } from "./emptraining/generateEmpTrainingPDF";
import { checklistSections } from "./emptraining/editEmpTraining";
import { generateAcuityRecordPDF } from "./visualAcuityRecord/generateAcuityRecordPDF";
import { generateIntegrityDeclarationPDF } from "./integrityDeclaration/generateIntegrityDeclarationPDF";
import { generateInterviewPersonnelPDF } from "./interviewPersonnel/generateInterviewPersonnelPDF";
import { generateTechnicalCompetencyPDF } from "./technicalCompetencyRecord/generateTechnicalCompetencyPDF";
import { generateOfferJoiningPDF } from "../../TalentAcquisition&Development/TalentAcquisition/OfferJoining/offerJoiningPDF";
import { generateApplicationFormPDF } from "./applicationFormPDF";
import { formateDate } from "../../../utils/commonComponent/commonConstant";
import { parseNum } from "../../TalentAcquisition&Development/TalentAcquisition/OfferJoining/offerJoiningUtils";
import { defaultNote16, defaultNote17 } from "../../TalentAcquisition&Development/TalentAcquisition/OfferJoining/editOfferJoining";
import { generateCurriculumVitaePDF } from "../curriculumVitae/generateCurriculumVitaePDF";
import generateInterviewAssessmentPDF from "../interviewAssessment/generateInterviewAssessmentpdf";
import generateJobDescriptionPDF from "../jobDescription/generatejobDescriptionPdf";

// Shared designation resolver — same logic used inside the Edit screens.
const resolveDesignation = async (employeeId) => {
  if (!employeeId) return "";
  try {
    const res = await fetchEmployeeOtherData(employeeId);
    return res?.[0]?.hrDesignation || "";
  } catch (err) {
    console.log(err);
    return "";
  }
};

const DownloadReportsModal = ({ open, onClose, employeeId, employeeName }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [activeKey, setActiveKey] = React.useState(null);
  const [empType, setEmpType] = React.useState(1);

  React.useEffect(() => {
    if (!open || !employeeId) return;
    const checkEmpType = async () => {
      try {
        const [otherData] = await fetchQuery(
          `SELECT employeeType FROM employeeOtherData WHERE employeeId = ${employeeId}`
        );
        if (otherData?.employeeType) {
          setEmpType(Number(otherData.employeeType));
          return;
        }
        const [formsRow] = await fetchQuery(
          `SELECT offerJoiningUrl, directOfferUrl FROM EmployeeForms WHERE employeeID = '${employeeId}' LIMIT 1`
        );
        const url = formsRow?.offerJoiningUrl || formsRow?.directOfferUrl;
        if (url) {
          const d = await fetchJsonFromUrl(url);
          if (d?.employeeType) {
            setEmpType(Number(d.employeeType));
            return;
          }
        }
        const [emp] = await fetchQuery(`SELECT offerId FROM employees WHERE id = ${employeeId}`);
        if (emp?.offerId) {
          const rawOfferId = String(emp.offerId).trim();
          const numId = rawOfferId.replace(/\D/g, "");
          const numClause = numId ? `OR id = '${numId}'` : "";
          const [o] = await fetchQuery(
            `SELECT employmentType, contractFees FROM talentAcqOffer WHERE offerId = '${rawOfferId}' OR id = '${rawOfferId}' ${numClause} LIMIT 1`
          );
          if (o) {
            const isContractOffer = Number(o.contractFees) > 0 || String(o.employmentType).includes("2");
            setEmpType(isContractOffer ? 2 : 1);
            return;
          }
        }
        setEmpType(1);
      } catch (err) {
        console.error("Error determining employee type for download reports modal:", err);
        setEmpType(1);
      }
    };
    checkEmpType();
  }, [open, employeeId]);

  if (!open) return null;

  // ── Training Feedback ──
  const downloadTrainingFeedback = async () => {
    try {
      setActiveKey("trainingFeedback");
      setLoading(true);

      const [employees, otherDataRes, feedback] = await Promise.all([
        fetchEmployeeDetails(employeeId),
        fetchEmployeeOtherData(employeeId),
        fetchEmpTrainingFeedback(employeeId),
      ]);

      await generateTrainingFeedbackPDF(
        feedback?.[0] || {},
        employees?.[0] || {},
        otherDataRes?.[0] || {},
        setLoading
      );
    } catch (err) {
      console.log(err);
      setLoading(false);
    } finally {
      setActiveKey(null);
    }
  };

  // ── Asset Issue Record ──
  const downloadAssetIssueRecord = async () => {
    try {
      setActiveKey("assetIssueRecord");
      setLoading(true);

      const [employees, otherDataRes, empList, items] = await Promise.all([
        fetchEmployeeDetails(employeeId),
        fetchEmployeeOtherData(employeeId),
        fetchEmployeeDetails(),
        fetchAssetRecords(employeeId),
      ]);

      const empRecord = employees?.[0] || {};
      const otherRecord = otherDataRes?.[0] || {};
      const employeeOptions = (empList || []).map((e) => ({
        id: e.id,
        label: e.name,
        signature: e.signature || "",
      }));

      let reportingManagerName = "";
      if (otherRecord?.reportingManagerId) {
        const reportingManager = await fetchQuery(
          `select id, name from employees where status = 1 and id = ${otherRecord.reportingManagerId}`
        );
        reportingManagerName = reportingManager?.[0]?.name || "";
      }

      await generateAssetIssueRecordPDF(
        empRecord,
        { ...otherRecord, reportingManagerName },
        items?.length ? items : [],
        employeeOptions,
        setLoading
      );
    } catch (err) {
      console.log(err);
      setLoading(false);
    } finally {
      setActiveKey(null);
    }
  };

  // ── Surveyor On-Site Monitoring Report ──
  const downloadSurveyorReport = async () => {
    try {
      setActiveKey("surveyorReport");
      setLoading(true);

      const [employees, otherDataRes, empList] = await Promise.all([
        fetchEmployeeDetails(employeeId),
        fetchEmployeeOtherData(employeeId),
        fetchEmployeeDetails(),
      ]);

      const employee = employees?.[0] || {};
      const otherData = otherDataRes?.[0] || {};
      const employeeOptions = (empList || []).map((e) => ({
        id: e.id,
        label: e.name,
        signature: e.signature || "",
      }));

      const report = await fetchSurveyorMonitoringReport(employeeId);
      const record = report?.[0] || {};

      const resolvedMonitorName =
        record?.monitorName ||
        employeeOptions.find((o) => o.id == record?.monitorId)?.label ||
        "";
      const resolvedTechnicalManagerName =
        record?.technicalManagerName ||
        employeeOptions.find((o) => o.id == record?.SignedTechnicalManagerId)?.label ||
        "";

      const [resolvedMonitorDesignation, resolvedTechnicalManagerDesignation] = await Promise.all([
        resolveDesignation(record?.monitorId),
        resolveDesignation(record?.SignedTechnicalManagerId),
      ]);

      const inputStates = {
        ...record,
        monitorName: resolvedMonitorName,
        monitorDesignation: resolvedMonitorDesignation,
        technicalManagerName: resolvedTechnicalManagerName,
        technicalManagerDesignation: resolvedTechnicalManagerDesignation,
      };

      const referenceDocs = REFERENCE_DOC_DEFS.map((doc) => ({
        ...doc,
        value: record?.[doc.key] || "",
      }));

      const items = await fetchSurveyorItems(employeeId);
      const itemRows = items?.length ? items : [];

      const formsRes = await fetchQuery(
        `SELECT * FROM EmployeeForms WHERE employeeID = ${employeeId}`
      );
      const formsRecord = formsRes?.[0] || {};

      let savedQuestions = [];
      if (formsRecord?.surveyorOnSiteUlr) {
        const fileData = await fetchJsonFromUrl(formsRecord.surveyorOnSiteUlr);
        savedQuestions = Array.isArray(fileData) ? fileData : (fileData?.questions || []);
      }
      const questions = mergeWithDefs(flattenQuestionKeys(), savedQuestions);

      await generateSurveyorMonitoringReportPDF(
        inputStates,
        employee,
        otherData,
        referenceDocs,
        itemRows,
        questions,
        employeeOptions,
        setLoading
      );
    } catch (err) {
      console.log(err);
      setLoading(false);
    } finally {
      setActiveKey(null);
    }
  };

  // ── NDT Certificate of Qualification — one separate PDF per method type ──
  const downloadNdtMethod = (methodType) => async () => {
    const key = `ndt-${methodType}`;
    try {
      setActiveKey(key);
      setLoading(true);

      const [employees, otherDataRes, savedMethods, qualifications] = await Promise.all([
        fetchEmployeeDetails(employeeId),
        fetchEmployeeOtherData(employeeId),
        fetchEmployeeNdtMethods(employeeId),
        fetchEmployeeQualification(employeeId),
      ]);

      const employee = employees?.[0] || {};
      const otherData = otherDataRes?.[0] || {};
      const qualification = qualifications?.[0] || {};

      const merged = mergeNdtMethodRows(savedMethods || []);
      const methodRow =
        merged.find((r) => r.methodType === methodType) || emptyNdtMethodRow(methodType);

      await generateNdtQualificationPDF(
        [methodRow],
        employee,
        otherData,
        qualification,
        setLoading
      );
    } catch (err) {
      console.log(err);
      setLoading(false);
    } finally {
      setActiveKey(null);
    }
  };

  //  Employee Induction Training Record
  const downloadInductionTraining = async () => {
    try {
      setActiveKey("inductionTraining");
      setLoading(true);

      const formsRes = await fetchQuery(
        `SELECT * FROM EmployeeForms WHERE employeeID = ${employeeId}`
      );
      const formsRecord = formsRes?.[0] || {};

      if (!formsRecord?.inductionTrainingUrl) {
        toast.warning("Induction Training Record has not been submitted for this employee yet.");
        return;
      }

      const d = await fetchJsonFromUrl(formsRecord.inductionTrainingUrl);
      const doc = await generateEmpTrainingPDF(d || {}, d?.checklistData || {}, checklistSections);
      doc.save(`Induction-Training-Record-${d?.employeeName || employeeName || "Employee"}.pdf`);
    } catch (err) {
      console.log(err);
      toast.error("Failed to download Induction Training Record PDF");
    } finally {
      setLoading(false);
      setActiveKey(null);
    }
  };

  //  Visual Acuity Record 
  const downloadVisualAcuity = async () => {
    try {
      setActiveKey("visualAcuity");
      setLoading(true);

      const formsRes = await fetchQuery(
        `SELECT * FROM EmployeeForms WHERE employeeID = ${employeeId}`
      );
      const formsRecord = formsRes?.[0] || {};

      if (!formsRecord?.acuityRecordUrl) {
        toast.warning("Visual Acuity Record has not been submitted for this employee yet.");
        return;
      }

      const d = await fetchJsonFromUrl(formsRecord.acuityRecordUrl);
      const doc = await generateAcuityRecordPDF(d || {});
      doc.save(`Visual-Acuity-Record-${d?.candidateName || employeeName || "Candidate"}.pdf`);
    } catch (err) {
      console.log(err);
      toast.error("Failed to download Visual Acuity Record PDF");
    } finally {
      setLoading(false);
      setActiveKey(null);
    }
  };

  //  Integrity Declaration 
  const downloadIntegrityDeclaration = async () => {
    try {
      setActiveKey("integrityDeclaration");
      setLoading(true);

      const formsRes = await fetchQuery(
        `SELECT * FROM EmployeeForms WHERE employeeID = ${employeeId}`
      );
      const formsRecord = formsRes?.[0] || {};

      if (!formsRecord?.integrityDeclarationUrl) {
        toast.warning("Integrity Declaration has not been submitted for this employee yet.");
        return;
      }

      const d = await fetchJsonFromUrl(formsRecord.integrityDeclarationUrl);
      const doc = await generateIntegrityDeclarationPDF(d || {});
      doc.save(`Integrity-Declaration-${d?.employeeName || employeeName || "Employee"}.pdf`);
    } catch (err) {
      console.log(err);
      toast.error("Failed to download Integrity Declaration PDF");
    } finally {
      setLoading(false);
      setActiveKey(null);
    }
  };

  //  Interview Evaluation of Personnel 
  const downloadInterviewEvaluation = async () => {
    try {
      setActiveKey("interviewEvaluation");
      setLoading(true);

      const formsRes = await fetchQuery(
        `SELECT * FROM EmployeeForms WHERE employeeID = ${employeeId}`
      );
      const formsRecord = formsRes?.[0] || {};

      if (!formsRecord?.interviewEvaluationUrl) {
        toast.warning("Interview Evaluation has not been submitted for this employee yet.");
        return;
      }

      const d = await fetchJsonFromUrl(formsRecord.interviewEvaluationUrl);
      const doc = await generateInterviewPersonnelPDF(d || {});
      const safeName = (d?.candidateName || employeeName || "Candidate").replace(/[^a-zA-Z0-9_-]/g, "_");
      doc.save(`Interview_Evaluation_${safeName}.pdf`);
    } catch (err) {
      console.log(err);
      toast.error("Failed to download Interview Evaluation PDF");
    } finally {
      setLoading(false);
      setActiveKey(null);
    }
  };

  //  Technical Competency Record 
  const downloadTechnicalCompetency = async () => {
    try {
      setActiveKey("technicalCompetency");
      setLoading(true);

      const formsRes = await fetchQuery(
        `SELECT * FROM EmployeeForms WHERE employeeID = ${employeeId}`
      );
      const formsRecord = formsRes?.[0] || {};

      if (!formsRecord?.competencyRecordUrl) {
        toast.warning("Technical Competency Record has not been submitted for this employee yet.");
        return;
      }

      const d = await fetchJsonFromUrl(formsRecord.competencyRecordUrl);
      const doc = await generateTechnicalCompetencyPDF(d || {});
      const safeName = (d?.candidateName || employeeName || "Surveyor").replace(/[^a-zA-Z0-9_-]/g, "_");
      doc.save(`Technical_Competency_Record_${safeName}.pdf`);
    } catch (err) {
      console.log(err);
      toast.error("Failed to download Technical Competency Record PDF");
    } finally {
      setLoading(false);
      setActiveKey(null);
    }
  };

  // Offer & Joining Letters 
  const downloadOfferJoiningDoc = (docType, reportKey) => async () => {
    try {
      setActiveKey(reportKey);
      setLoading(true);

      const [emp] = await fetchQuery(`SELECT * FROM employees WHERE id = ${employeeId}`);
      const [otherData] = await fetchQuery(`SELECT * FROM employeeOtherData WHERE employeeId = ${employeeId}`);

      let offerRec = null;
      const offerIdVal = emp?.offerId || otherData?.offerId;
      if (offerIdVal) {
        const rawOfferId = String(offerIdVal).trim();
        const digits = rawOfferId.replace(/\D/g, "");
        const intId = digits ? parseInt(digits, 10) : null;
        const intClause = intId && !isNaN(intId) ? `OR id = ${intId}` : "";
        try {
          const [o] = await fetchQuery(
            `SELECT * FROM talentAcqOffer 
             WHERE offerId = '${rawOfferId}' 
                OR id = '${rawOfferId}' 
                ${intClause}
             ORDER BY id DESC LIMIT 1`
          );
          offerRec = o || null;
        } catch (e) {
          console.error(e);
        }
      }

      // Fallback: search by candidate name, mobile, email
      if (!offerRec && emp) {
        const empName = emp.name?.trim();
        const empMobile = (emp.contactNumber || emp.alternativeContactNumber || "").trim();
        const empEmail = (emp.emailAddress || emp.officeEmailAddress || "").trim();

        const matchConditions = [];
        if (empName) {
          matchConditions.push(`candidateName = '${empName}'`);
          matchConditions.push(`candidateName LIKE '%${empName}%'`);
          matchConditions.push(`'${empName}' LIKE CONCAT('%', candidateName, '%')`);
        }
        if (empMobile) {
          matchConditions.push(`mobile = '${empMobile}'`);
        }
        if (empEmail) {
          matchConditions.push(`email = '${empEmail}'`);
        }

        if (matchConditions.length > 0) {
          try {
            const [matched] = await fetchQuery(
              `SELECT * FROM talentAcqOffer 
               WHERE ${matchConditions.join(" OR ")}
               ORDER BY id DESC LIMIT 1`
            );
            if (matched) offerRec = matched;
          } catch (e) {
            console.error(e);
          }
        }
      }

      // Optional extra pipeline & candidate data
      let pipeInfo = null;
      let candInfo = null;
      if (offerRec?.pipelineId) {
        try {
          const [pipe] = await fetchQuery(
            `SELECT * FROM talentAcqPipeline WHERE id = ${offerRec.pipelineId}`
          );
          pipeInfo = pipe || null;
          if (pipe?.candId) {
            const [cand] = await fetchQuery(
              `SELECT * FROM talentAcqCandidate WHERE id = ${pipe.candId}`
            );
            candInfo = cand || null;
          }
        } catch (pErr) {
          console.error("Error fetching pipeline info in download modal:", pErr);
        }
      }

      // Check saved JSON in EmployeeForms
      let savedJson = null;
      try {
        const [formsRow] = await fetchQuery(
          `SELECT offerJoiningUrl, directOfferUrl FROM EmployeeForms WHERE employeeID = '${employeeId}' LIMIT 1`
        );
        const urlToFetch = formsRow?.offerJoiningUrl || formsRow?.directOfferUrl;
        if (urlToFetch) {
          savedJson = await fetchJsonFromUrl(urlToFetch);
        }
      } catch (e) {
        console.error(e);
      }

      const resolvedEmpType = savedJson?.employeeType || otherData?.employeeType || (offerRec?.isContract ? 2 : 1) || empType;
      const docIsContract = Number(resolvedEmpType) === 2;

      const targetEmpId = employeeId;
      const offerRefCode = docIsContract
        ? `DYC/HR-OFFER-FTC/${String(targetEmpId).padStart(3, "0")}`
        : `DYC/HR-OFFER-PR/${String(targetEmpId).padStart(3, "0")}`;

      const apptRefCode = `DYC/HR-APPT/${String(targetEmpId).padStart(3, "0")}`;
      const jkRefCode = `DYC/HR-JK/DYC-${String(targetEmpId).padStart(4, "0")}`;

      const rawOfferDate = savedJson?.offerDate || offerRec?.offerDate || emp?.offerDate || new Date().toISOString().split("T")[0];
      const rawJoinDate = savedJson?.joiningDate || offerRec?.confirmedJoiningDate || offerRec?.actualJoiningDate || otherData?.joiningDate || emp?.dateOfJoining || "";

      const offerDateFormatted = formateDate(rawOfferDate) || rawOfferDate;
      const joiningDateFormatted = formateDate(rawJoinDate) || rawJoinDate;
      const offerAcceptanceDateFormatted = savedJson?.offerAcceptanceDate ? (formateDate(savedJson.offerAcceptanceDate) || savedJson.offerAcceptanceDate) : "";
      const apptAcceptanceDateFormatted = savedJson?.apptAcceptanceDate ? (formateDate(savedJson.apptAcceptanceDate) || savedJson.apptAcceptanceDate) : "";

      // Remuneration numbers
      const numBasic = parseNum((savedJson?.basicSalary !== "" && savedJson?.basicSalary != null) ? savedJson.basicSalary : offerRec?.basicSalary);
      const numHouse = parseNum((savedJson?.houseAllow !== "" && savedJson?.houseAllow != null) ? savedJson.houseAllow : offerRec?.houseAllow);
      const numEdu = parseNum((savedJson?.educationAllow !== "" && savedJson?.educationAllow != null) ? savedJson.educationAllow : offerRec?.educationAllow);
      const numMed = parseNum((savedJson?.medicalReimb !== "" && savedJson?.medicalReimb != null) ? savedJson.medicalReimb : offerRec?.medicalReimb);
      const numConv = parseNum((savedJson?.conveyance !== "" && savedJson?.conveyance != null) ? savedJson.conveyance : offerRec?.conveyance);
      const numPersonal = parseNum((savedJson?.personalAllow !== "" && savedJson?.personalAllow != null) ? savedJson.personalAllow : offerRec?.personalAllow);
      const numCity = parseNum((savedJson?.cityCompAllow !== "" && savedJson?.cityCompAllow != null) ? savedJson.cityCompAllow : offerRec?.cityCompAllow);
      const numOther = parseNum((savedJson?.otherAllow !== "" && savedJson?.otherAllow != null) ? savedJson.otherAllow : offerRec?.otherAllow);

      const subAM = numBasic + numHouse + numEdu + numMed + numConv + numPersonal + numCity + numOther;
      const subAY = subAM * 12;

      const numPF = parseNum((savedJson?.companyPF !== "" && savedJson?.companyPF != null) ? savedJson.companyPF : offerRec?.companyPF);
      const pfY = numPF * 12;
      const gratuityY = numBasic > 0 ? Math.round(numBasic * 12 * 0.0481) : 0;
      const numHealth = parseNum((savedJson?.annualHealthCheck !== "" && savedJson?.annualHealthCheck != null) ? savedJson.annualHealthCheck : offerRec?.annualHealthCheck);
      const numPerfo = parseNum((savedJson?.perfoIncentive !== "" && savedJson?.perfoIncentive != null) ? savedJson.perfoIncentive : offerRec?.perfoIncentive);

      const subBY = pfY + gratuityY + numHealth + numPerfo;
      const totalCTC = subAY + subBY;

      const numCFees = parseNum((savedJson?.contractFees !== "" && savedJson?.contractFees != null) ? savedJson.contractFees : offerRec?.contractFees);
      const numCOther = parseNum((savedJson?.contractOtherAllow !== "" && savedJson?.contractOtherAllow != null) ? savedJson.contractOtherAllow : offerRec?.contractOtherAllow);
      const numCMobile = parseNum((savedJson?.mobileConnect !== "" && savedJson?.mobileConnect != null) ? savedJson.mobileConnect : offerRec?.mobileConnect);
      const numCMed = parseNum((savedJson?.medical !== "" && savedJson?.medical != null) ? savedJson.medical : offerRec?.medical);

      const contractTotalM = numCFees + numCOther + numCMobile + numCMed;
      const contractTotalY = contractTotalM * 12;

      const payload = {
        id: targetEmpId,
        candidateName: savedJson?.candidateName || emp?.name || offerRec?.candidateName || offerRec?.pipeCandName || employeeName || "",
        candidateAddress: savedJson?.address || emp?.currentAddress || emp?.permenantAddress || offerRec?.address || offerRec?.candLocation || "",
        candidateMobile: savedJson?.mobile || emp?.contactNumber || emp?.alternativeContactNumber || offerRec?.mobile || offerRec?.candMobile || "",
        candidateEmail: savedJson?.email || emp?.emailAddress || emp?.officeEmailAddress || offerRec?.email || offerRec?.candEmail || "",
        candidateLocation: savedJson?.location || emp?.location || offerRec?.client || offerRec?.pipeClient || "Thane, Maharashtra",
        position: savedJson?.position || otherData?.hrDesignation || emp?.designation || offerRec?.position || offerRec?.pipePosition || "",
        offerRefCode,
        apptRefCode,
        jkRefCode,
        offerDateFormatted,
        joiningDateFormatted,
        offerAcceptanceDateFormatted,
        apptAcceptanceDateFormatted,
        isContract: docIsContract,
        docType,

        // Offer Notes
        note16: savedJson?.note16 || offerRec?.note16 || defaultNote16,
        note17: savedJson?.note17 || offerRec?.note17 || defaultNote17,

        // Contract
        contractFees: String(numCFees),
        contractOtherAllow: String(numCOther),
        mobileConnect: String(numCMobile),
        medical: String(numCMed),

        // Payroll
        basicSalary: String(numBasic),
        houseAllow: String(numHouse),
        educationAllow: String(numEdu),
        medicalReimb: String(numMed),
        conveyance: String(numConv),
        personalAllow: String(numPersonal),
        cityCompAllow: String(numCity),
        otherAllow: String(numOther),
        subTotalA: String(subAM),
        subAM: String(subAM),
        subAY: String(subAY),
        companyPF: String(numPF),
        companyGratuity: String(gratuityY),
        annualHealthCheck: String(numHealth),
        perfoIncentive: String(numPerfo),
        subTotalB: String(subBY),
        subBY: String(subBY),
        ctcOffered: docIsContract ? String(contractTotalY) : String(totalCTC),
        totalY: docIsContract ? String(contractTotalY) : String(totalCTC),
      };

      await generateOfferJoiningPDF(payload, setLoading);
    } catch (err) {
      console.error(err);
      toast.error("Failed to download letter PDF");
    } finally {
      setLoading(false);
      setActiveKey(null);
    }
  };

  const isContractEmp = Number(empType) === 2;
  const offerJoiningReports = isContractEmp
    ? [
        {
          key: "contractOffer",
          label: "Contract Offer / Service Agreement Letter",
          action: downloadOfferJoiningDoc("offer", "contractOffer"),
        },
        {
          key: "contractJoining",
          label: "Contract Joining Kit Letter",
          action: downloadOfferJoiningDoc("joining", "contractJoining"),
        },
      ]
    : [
        {
          key: "payrollOffer",
          label: "Full-time Offer Letter",
          action: downloadOfferJoiningDoc("offer", "payrollOffer"),
        },
        {
          key: "payrollAppt",
          label: "Appointment Letter",
          action: downloadOfferJoiningDoc("appointment", "payrollAppt"),
        },
        {
          key: "payrollJoining",
          label: "Joining Kit Letter",
          action: downloadOfferJoiningDoc("joining", "payrollJoining"),
        },
      ];

  // Employee Application Form
  const downloadApplicationForm = async () => {
    try {
      setActiveKey("applicationForm");
      setLoading(true);

      const [employee] = await fetchQuery(`SELECT * FROM employees WHERE id = ${employeeId}`);
      if (!employee) return;

      const detailTables = [
        "employeeQualification",
        "employeeNominee",
        "employeeEmergencyDetails",
        "employeeOtherData",
        "employeeDocuments",
      ];

      const [results, experienceRecords, prevHistRecords] = await Promise.all([
        Promise.all(detailTables.map((table) => fetchQuery(`SELECT * FROM ${table} WHERE employeeId = ${employeeId}`))),
        fetchQuery(`SELECT * FROM employeeExperience WHERE employeeId = ${employeeId}`),
        fetchQuery(`SELECT * FROM employeePreviousHistory WHERE employeeId = ${employeeId}`),
      ]);

      const mergedData = { ...employee };

      detailTables.forEach((table, index) => {
        const record = results[index]?.[0];
        if (record) {
          Object.entries(record).forEach(([key, value]) => {
            if (key !== "id" && !(key in mergedData)) {
              mergedData[key] = value;
            }
          });
        }
      });

      mergedData.experienceList = experienceRecords || [];
      mergedData.familyMembers = results[1] || [];

      mergedData.referees = (prevHistRecords || []).map((r) => ({
        id: r.id,
        name: r.Name || "",
        contactNo: r.contactNo || "",
        position: r.positionHeld || "",
      }));
      if (prevHistRecords && prevHistRecords.length > 0) {
        const firstRec = prevHistRecords[0];
        mergedData.agreeRefereesContact = firstRec.refereeContacted || firstRec.refereesContacted || mergedData.agreeRefereesContact;
        mergedData.workTypeAvailable = firstRec.workTypeAvailable || mergedData.workTypeAvailable;
        mergedData.noticePeriodOrJoiningDate = firstRec.joiningNoticeDate || firstRec.noticePeriodOrJoiningDate || mergedData.noticePeriodOrJoiningDate;
        mergedData.terminatedPastEmployer = firstRec.terminatedEmployer || firstRec.terminatedPastEmployer || mergedData.terminatedPastEmployer;
        mergedData.connectedWithBusiness = firstRec.connectedBusiness || firstRec.connectedWithBusiness || mergedData.connectedWithBusiness;
        mergedData.ownHouseArrangement = firstRec.ownHouseArrangement || mergedData.ownHouseArrangement;
        mergedData.serveIndiaAbroad = firstRec.serveIndiaAbroad || mergedData.serveIndiaAbroad;
        mergedData.friendsRelativesInDYC = firstRec.friendsInDYC || firstRec.friendsRelativesInDYC || mergedData.friendsRelativesInDYC;
        mergedData.whoReferredDYC = firstRec.whoReferredDYC || mergedData.whoReferredDYC;
        mergedData.interviewedBeforeDYC = firstRec.interviewedDYC || firstRec.interviewedBeforeDYC || mergedData.interviewedBeforeDYC;
        mergedData.interviewedBeforeDYCDate = firstRec.interviewedDYCDate || firstRec.interviewedBeforeDYCDate || mergedData.interviewedBeforeDYCDate;
        mergedData.relativeInGovtOrg = firstRec.relativeGovtOrg || firstRec.relativeInGovtOrg || mergedData.relativeInGovtOrg;
        mergedData.relativeInGovtOrgDetails = firstRec.relativeGovtDetails || firstRec.relativeInGovtOrgDetails || mergedData.relativeInGovtOrgDetails;
        mergedData.relativeOwnBusiness = firstRec.relativeBusiness || firstRec.relativeOwnBusiness || mergedData.relativeOwnBusiness;
        mergedData.relativeOwnBusinessDetails = firstRec.relativeBusinessDetails || firstRec.relativeOwnBusinessDetails || mergedData.relativeOwnBusinessDetails;
        mergedData.potentialConflictOfInterest = firstRec.conflictOfInterest || firstRec.potentialConflictOfInterest || mergedData.potentialConflictOfInterest;
      }

      const offerIdVal = mergedData.offerId;
      if (offerIdVal) {
        try {
          const [offerRec] = await fetchQuery(
            `SELECT * FROM talentAcqOffer WHERE offerId = '${offerIdVal}' OR id = '${offerIdVal}' LIMIT 1`
          );
          if (offerRec) {
            mergedData.offerDate = offerRec.offerDate;
            mergedData.ctcOffered = offerRec.ctcOffered || mergedData.ctcOffered;
            mergedData.certifications = offerRec.certifications || mergedData.certifications;
            mergedData.noticePeriod = offerRec.noticePeriod || mergedData.noticePeriod;
            mergedData.dateReportingToWork = offerRec.actualJoiningDate || offerRec.confirmedJoiningDate || mergedData.dateOfJoining;
            mergedData.actualJoiningDate = offerRec.actualJoiningDate || offerRec.confirmedJoiningDate || mergedData.actualJoiningDate;
            if (offerRec.position) {
              mergedData.hrDesignation = offerRec.position || mergedData.hrDesignation;
              mergedData.designation = offerRec.position || mergedData.designation;
            }
          }
        } catch (e) {
          console.error("Error enriching offer details for application form:", e);
        }
      }

      const pdfDoc = await generateApplicationFormPDF(mergedData || {});
      const fileName = `Application_Form_${mergedData?.name || employeeName || "Employee"}.pdf`;
      pdfDoc.save(fileName);
    } catch (err) {
      console.error(err);
      toast.error("Failed to download Application Form PDF");
    } finally {
      setLoading(false);
      setActiveKey(null);
    }
  };

  //  ── Curriculum Vitae ──
  const downloadCurriculumVitae = async () => {
    try {
      setActiveKey("curriculumVitae");
      setLoading(true);

      const employeeForms = await fetchEmployeeForms(employeeId);
      const formRecord = employeeForms?.[0] || {};

      const curriculumVitalUrl = formRecord?.curriculumVitalUrl;
      if (!curriculumVitalUrl) {
        toast.warning("No curriculumVital found for this employee");
        return;
      }

      const fetchData = await fetchJsonFromUrl(curriculumVitalUrl);

      const pdf = await generateCurriculumVitaePDF(fetchData);
      pdf.save("Curriculum-Vitae.pdf");
    } catch (err) {
      console.log(err);
      setLoading(false);
    } finally {
      setActiveKey(null);
      setLoading(false);
    }
  };

  // ── Interview Assessment ──
  const downloadInterviewAssessment = async () => {
    try {
      setActiveKey("interviewAssessment");
      setLoading(true);

      const employeeForms = await fetchEmployeeForms(employeeId);
      const formRecord = employeeForms?.[0] || {};

      const interviewAssessmentUrl = formRecord?.interviewAssessmentUrl;
      if (!interviewAssessmentUrl) {
        toast.warning("No interviewAssessment found for this employee");
        return;
      }

      const fetchData = await fetchJsonFromUrl(interviewAssessmentUrl);

      const pdf = await generateInterviewAssessmentPDF(fetchData);
      pdf.save("Interview-Assessment.pdf");
    } catch (err) {
      console.log(err);
      setLoading(false);

    } finally {
      setActiveKey(null);
      setLoading(false);
    }
  };

  // ── Job Description ──
  const downloadJobDescription = async () => {
    try {
      setActiveKey("jobDescription");
      setLoading(true);

      const employeeJobDescription = await fetchEmployeeJobDescription(employeeId);

      if (!employeeJobDescription?.length) {
        toast.warning("No job description found for this employee");
        return;
      }

      const jobDescriptionRecord = employeeJobDescription?.[0] || {};


      const pdf = await generateJobDescriptionPDF(jobDescriptionRecord);
      pdf.save("Job-Description.pdf");
    } catch (err) {
      console.log(err);
      setLoading(false);
    } finally {
      setActiveKey(null);
      setLoading(false);
    }
  };

  const reports = [
    { key: "applicationForm", label: "Employee Application Form", action: downloadApplicationForm },
    { key: "curriculumVitae", label: "Curriculum Vitae", action: downloadCurriculumVitae },
    { key: "jobDescription", label: "Job Description", action: downloadJobDescription },
    { key: "interviewAssessment", label: "Interview Assessment", action: downloadInterviewAssessment },
    { key: "trainingFeedback", label: "Training Feedback", action: downloadTrainingFeedback },
    { key: "assetIssueRecord", label: "Asset Issue Record", action: downloadAssetIssueRecord },
    { key: "surveyorReport", label: "Surveyor On-Site Monitoring Report", action: downloadSurveyorReport },
    ...NDT_METHOD_TYPES.map((m) => ({
      key: `ndt-${m.value}`,
      label: `NDT Qualification - ${m.label}`,
      action: downloadNdtMethod(m.value),
    })),
    { key: "inductionTraining", label: "Induction Training Record", action: downloadInductionTraining },
    { key: "visualAcuity", label: "Visual Acuity Record", action: downloadVisualAcuity },
    { key: "integrityDeclaration", label: "Integrity Declaration", action: downloadIntegrityDeclaration },
    { key: "interviewEvaluation", label: "Interview Evaluation of Personnel", action: downloadInterviewEvaluation },
    { key: "technicalCompetency", label: "Technical Competency Record", action: downloadTechnicalCompetency },
    ...offerJoiningReports,
  ];

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <div className="text-base font-semibold text-gray-800">Download Reports</div>
              {employeeName ? (
                <div className="text-xs text-gray-500 mt-0.5">{employeeName}</div>
              ) : null}
            </div>
            <CloseIcon
              className="cursor-pointer text-gray-500 hover:text-gray-800"
              fontSize="small"
              onClick={onClose}
            />
          </div>

          <div className="flex flex-col divide-y divide-gray-200 max-h-[70vh] overflow-y-auto">
            {reports.map((r) => (
              <div key={r.key} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm text-gray-700">{r.label}</span>
                <button
                  type="button"
                  disabled={loading}
                  onClick={r.action}
                  className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DownloadIcon fontSize="small" />
                  {loading && activeKey === r.key ? "Preparing..." : "Download"}
                </button>
              </div>
            ))}
          </div>

          <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <DownloadPdfLoaderModal loading={loading} />
    </>
  );
};

export default DownloadReportsModal;